import { inject, Injectable, signal } from '@angular/core';
import {
  convex as turfConvex,
  featureCollection as turfFeatureCollection,
  point as turfPoint,
} from '@turf/turf';
import { GeoJSONSourceSpecification, LayerSpecification } from 'mapbox-gl';
import { Observable } from 'rxjs';
import { DevicesFilters, DevicesManagerService, FullDevice } from '../devices-manager.service';
import { MapService } from '../map.service';
import BaseLayer from './BaseLayer';
import { DevicesLayerService } from './devices-layer.service';
import { EventsDeviceUtilsService } from './events-devices-utils';

@Injectable({
  providedIn: 'root',
})
export class PolygonsDevicesLayer extends BaseLayer<FullDevice> {
  public override mapService = inject(MapService);
  private _devicesLayerService = inject(DevicesLayerService);
  private _devicesManagerService = inject(DevicesManagerService);
  private _eventsDeviceUtils = inject(EventsDeviceUtilsService);

  readonly _POLYCLUSTERS_FILLS_LAYER = this.mapService._POLYCLUSTERS_FILLS_LAYER;
  readonly _POLYCLUSTERS_BORDERS_LAYER = this.mapService._POLYCLUSTERS_BORDERS_LAYER;
  readonly _POLYCLUSTERS_COUNT_LAYER = this.mapService._POLYCLUSTERS_COUNT_LAYER;
  readonly _POLYCLUSTERS_DEVICES = this.mapService._POLYCLUSTERS_DEVICES;
  readonly _CLUSTERS_POLYGONS_SOURCE = 'clusters-polygons-source';
  readonly _DEVICES_SOURCE = 'devices-source-2';
  readonly _CLUSTER_MODE = 'polygon';

  public $visiblePoints = signal(null);

  public override properties: LayerSpecification & { source: string; type: LayerSpecification['type'] } = {
    id: this._POLYCLUSTERS_DEVICES,
    type: 'symbol',
    source: 'devices-source-2',
    filter: ['!', ['has', 'point_count']],
    paint: {
      'text-color': '#000000',
      'text-halo-color': '#fff',
      'text-halo-width': 2,
    },
    layout: {
      visibility: 'visible',
      'icon-image': 'device_{icon}',
      'icon-allow-overlap': true,
      'icon-size': 0.7, // 1
      'text-font': ['Open Sans Regular'],
      'text-field': '{registration}',
      'text-anchor': 'top',
      'text-size': 12,
      'text-offset': [0, 1.5], // [1, 1]
      'text-optional': true,
      'icon-pitch-alignment': 'map',
    },
  };

  // FILTERS MANAGEMENT //
  // find a more generic way to implement filters and displayConditions regards to CanvasPointLayer.
  public filters$: Observable<DevicesFilters>;

  constructor() {
    super({
      extraProperties: item =>
        ({
          movingStatus: item.movingStatus.value,
          registration: item.registration,
          driver_name: item.driver_name,
        }) as unknown,
    });

    this.properties.source = this._DEVICES_SOURCE; // call it here because cannot into super(...) above

    this.items = this._devicesManagerService.devices$ as unknown as BaseLayer<FullDevice>['items']; // TODO : fix dumb type

    // Commented waiting approbation of clusters...
    // this.mapService.eventCreateMap$.subscribe(value => {
    //   if (value) {
    //     if (this.mapService.map._loaded) {
    //       this.init();
    //     } else
    //       this.mapService.map.on('load', () => {
    //         this.init();
    //       });
    //   }
    // });

    this.mapService.waitForLayer(this.properties.id, () => {
      if (this.mapService.clusterMode === this._CLUSTER_MODE) this.show();
      else this.hide();

      this.filters$ = this._devicesLayerService.filters$;
      this.filters$.subscribe(value => {
        if (value) this.onFiltersChange(value);
      });

      this._setupEvents();
    });
  }

  public override show() {
    this.mapService.showLayers([
      this.properties.id,
      this._POLYCLUSTERS_FILLS_LAYER,
      this._POLYCLUSTERS_BORDERS_LAYER,
      this._POLYCLUSTERS_COUNT_LAYER,
    ]);

    this.hided = false;

    this._processClusters();

    this._orderLayers();

    return this;
  }

  public override hide() {
    this.mapService.hideLayers([
      this.properties.id,
      this._POLYCLUSTERS_FILLS_LAYER,
      this._POLYCLUSTERS_BORDERS_LAYER,
      this._POLYCLUSTERS_COUNT_LAYER,
    ]);

    this.hided = true;

    this._processClusters();

    return this;
  }

  public toggleLabel(preference?: string) {
    this._eventsDeviceUtils.toggleLabel(this.properties.id, preference);
  }

  private _setupEvents() {
    this.mapService.map.on('move', () => this._processClusters());
    this.mapService.map.on('zoomEnd', () => this._processClusters());
    this.onSourceUpdate = () => this._processClusters();

    this.onClick(event => {
      if (!this.hided) {
        const feature = event.features[0];
        this._eventsDeviceUtils.displayDevice(feature);
      }
    });

    this.onMouseEnter(event => {
      const feature = event.features[0];
      this._eventsDeviceUtils.displayPopup(feature);
    });

    this.onMouseLeave(() => {
      this._eventsDeviceUtils.removePopup();
    });

    this.mapService.map.on('zoom', () => {
      this.$visiblePoints.set(this.getCurrentVisiblePoints());
    });
  }

  private _setupLayers() {
    this.mapService.map.addLayer({
      id: this._POLYCLUSTERS_FILLS_LAYER,
      type: 'fill',
      source: this._CLUSTERS_POLYGONS_SOURCE,
      paint: {
        'fill-color': 'hsl(180, 17%, 98%)',
        fillOpacity: 2,
      },
    });

    this.mapService.map.addLayer({
      id: this._POLYCLUSTERS_BORDERS_LAYER,
      type: 'line',
      source: this._CLUSTERS_POLYGONS_SOURCE,
      paint: {
        'line-color': 'hsl(186, 100%, 25%)',
        'line-width': 2,
      },
    });

    this.mapService.map.addLayer({
      id: this._POLYCLUSTERS_COUNT_LAYER,
      type: 'symbol',
      source: this._DEVICES_SOURCE,
      filter: ['>', ['get', 'point_count'], 2],
      layout: {
        'text-field': '{point_count_abbreviated}',
        'text-font': this.mapService.fonts,
        'text-size': 12,
      },
      paint: {
        'text-color': 'hsl(186, 100%, 25%)',
      },
    });
  }

  private async _processClusters() {
    if (this.hided) {
      this._clearClusterPolygons();
      return;
    }

    const map = this.mapService.map;
    const source = map.getSource(this.properties.source);

    if (!source || !source.getClusterLeaves) {
      console.warn('Source not ready or not a GeoJSON source');
      return;
    }

    const features = map.querySourceFeatures(this.properties.source);

    const clusterFeatures = features.filter(feature => feature.properties?.cluster);

    if (clusterFeatures.length === 0) {
      this._clearClusterPolygons();
      return;
    }

    const polygons = {
      type: 'FeatureCollection',
      features: [],
    };

    // 2. TRANSFORM ALL CLUSTERS INTO A HULL CONVEX
    const clusterPromises = clusterFeatures.map(feature => {
      const clusterId = feature.properties.cluster_id;

      return new Promise(resolve => {
        const currentSource = map.getSource(this.properties.source);

        if (!currentSource || !currentSource.getClusterLeaves) {
          resolve(null);
          return;
        }

        currentSource.getClusterLeaves(clusterId, Infinity, 0, (err, clusterFeatures) => {
          if (err || !clusterFeatures || clusterFeatures.length === 0) {
            resolve(null);
            return;
          }

          try {
            const points = turfFeatureCollection(clusterFeatures.map(f => turfPoint(f.geometry.coordinates)));

            const hull = turfConvex(points);

            if (hull) {
              hull.properties = {
                cluster_id: clusterId,
                point_count: clusterFeatures.length,
              };
              resolve(hull);
            } else {
              resolve(null);
            }
          } catch (error) {
            console.warn(`Error processing cluster ${clusterId}:`, error);
            resolve(null);
          }
        });
      });
    });

    try {
      const results = await Promise.all(clusterPromises);

      polygons.features = results.filter(result => result !== null);

      const polygonsSource = map.getSource(this._CLUSTERS_POLYGONS_SOURCE);

      // 3. UPDATE OR CREATE SOURCE AND LAYERS
      if (polygonsSource) {
        polygonsSource.setData(polygons);
      } else {
        map.addSource(this._CLUSTERS_POLYGONS_SOURCE, {
          type: 'geojson',
          data: polygons,
        });

        this._setupLayers();
      }
    } catch (error) {
      console.error('Error during process clusters:', error);
    }
  }

  private _clearClusterPolygons() {
    const polygonsSource = this.mapService.map.getSource(this._CLUSTERS_POLYGONS_SOURCE);
    if (polygonsSource) {
      polygonsSource.setData({
        type: 'FeatureCollection',
        features: [],
      });
    }
  }

  private onFiltersChange(filters: DevicesFilters) {
    const defaultFilters = { ...(filters || {}) };

    // FILTER CONSTRUCTION
    // TODO: make it more scalable.
    let mapFilters: (string | string[])[] = undefined;

    const unitsFilters = ['!in', 'uid_parent'].concat(defaultFilters.units as any);
    const statusFilters = ['in', 'movingStatus'].concat(defaultFilters.movingStatus);

    if (filters.units?.length || filters.movingStatus?.length) mapFilters = ['all'];
    if (filters.units?.length) mapFilters.push(unitsFilters);
    if (filters.movingStatus?.length) mapFilters.push(statusFilters);

    // SET FILTERS ON MAP
    if (this.mapService.map.getLayer(this.properties.id)) {
      this.mapService.map.setFilter(this.properties.id, mapFilters);
    }

    const style = this.mapService.map.getStyle();
    const sourceStyle = style.sources[this.properties.source] as GeoJSONSourceSpecification;

    if (sourceStyle)
      sourceStyle.cluster = !(defaultFilters.units.length || defaultFilters.movingStatus.length);

    this.mapService.map.setStyle(style);

    if (filters.units.length || filters.movingStatus.length) this.mapService.setClusterMode('inactive');
  }

  private _orderLayers() {
    if (this.mapService.map.getLayer(this.mapService._LANDMARKS_LAYER)) {
      this.mapService.map.moveLayer(this._POLYCLUSTERS_DEVICES);
      this.mapService.map.moveLayer(this._POLYCLUSTERS_FILLS_LAYER);
      this.mapService.map.moveLayer(this._POLYCLUSTERS_BORDERS_LAYER);
      this.mapService.map.moveLayer(this._POLYCLUSTERS_COUNT_LAYER);
    }
  }
}
